import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { getBookletSources, setBookletSections } from "../../services/bookletService";
import { getApiErrorCode } from "../../utils/apiError";
import { Button } from "../../components/forms/Buttons/Button";
import OverflowMenu from "../../components/ui/OverflowMenu";
import { cardClass, focusRing, iconButtonClass, linkButtonClass, Spinner, useMediaQuery } from "../Exams/TestBuilder/ui";
import { issueText, variantIssueText } from "./BookletCheck";
import { bookletErrorMessage, fieldClass, linkTapClass, sectionTitleClass, tapClass, useFocusAfterRender, variantHref } from "./bts";

const STALE = ["booklet_changed", "booklet_active", "exam_archived"];
const rowButtonClass = `${iconButtonClass} max-sm:h-11 max-sm:w-11`;
const selectClass = `${fieldClass} h-10 py-0 pl-3 pr-9 max-sm:h-11`;
// A subject row of the list and of the add list: the subject, and its controls beside it while the row has room for
// both (14rem + 26rem), under it otherwise. It follows the card's own width (the page's second column, the sidebar),
// not the screen's; the controls get the same width in every row (at most 32rem), so that their selects line up.
const subjectRowClass = "flex flex-wrap items-center gap-x-4 gap-y-2";
const subjectClass = "min-w-0 flex-[1_1_14rem]";
const controlsClass = "flex min-w-0 max-w-[32rem] flex-[1_1_26rem] items-center";
// A side action of the list ("+ Add"): blue text, no frame.
const addLinkClass = `inline-flex h-10 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11 ${focusRing}`;
const TITLE_ID = "bts-sections-title";
const SAVE_ID = "bts-sections-save";

// After a choice in a row's "⋯" that leaves the row on the page (moved on a phone, or its variant opened in a new tab),
// the focus goes back to that "⋯" (`testId`): Headless UI leaves it on the closing menu, which then unmounts.
function focusAfterMenu(testId) {
  let frames = 0;
  const step = () => {
    const active = document.activeElement;
    if (active?.closest?.("[role='menu']") && frames < 60) {
      frames += 1;
      requestAnimationFrame(step);
      return;
    }
    if (!active || active === document.body) document.querySelector(`[data-testid="${testId}"]`)?.focus();
  };
  requestAnimationFrame(step);
}

/**
 * How a saved section stands, in this order: its variant not confirmed; its own check failed; a blocking issue
 * of the booklet's check on it (a variant to confirm again, a heading missing…); a matrix that is not standard
 * (activation can accept it with a reason: amber); else its check passed. An active booklet prints as it was
 * frozen: there, what would block an activation is amber too.
 */
function sectionState(t, saved, issues, sections, active) {
  const uuid = saved.variant?.uuid;
  const own = (issues || []).filter((issue) => issue.blocking && uuid && issue.variant === uuid);
  const bad = active ? "note" : "bad";
  if (saved.variant?.status && saved.variant.status !== "confirmed") {
    return { tone: bad, text: t("bts.sections.not_confirmed"), title: t("bts.issues.variant_not_confirmed") };
  }
  if (!saved.check?.ok) {
    return { tone: bad, text: t("bts.sections.check_failed"), title: variantIssueText(t, saved.check?.issues) };
  }
  const blocking = own.find((issue) => !(issue.code === "not_standard" && issue.overridable));
  if (blocking) {
    return {
      tone: bad,
      text: blocking.code === "question_changed" ? t("bts.sections.reconfirm") : t("bts.sections.has_problem"),
      title: issueText(t, blocking, sections, { active }),
    };
  }
  const standard = own.find((issue) => issue.code === "not_standard");
  if (standard) return { tone: "note", text: t("bts.sections.not_standard"), title: issueText(t, standard, sections) };
  return { tone: "ok", text: t("bts.sections.check_ok"), title: "" };
}

// The questions and points of a section, and how it stands (sectionState).
function SectionStats({ saved, info, issues, sections, active = false }) {
  const [t] = useTranslation();
  if (!saved) {
    return (
      <span className="text-slate-500">
        {info?.questions_count !== undefined && `${t("bts.sections.questions", { count: info.questions_count })} · `}
        {t("bts.sections.unsaved_stats")}
      </span>
    );
  }
  const state = sectionState(t, saved, issues, sections, active);
  const Icon = state.tone === "ok" ? CheckCircleIcon : state.tone === "note" ? ExclamationTriangleIcon : XCircleIcon;
  const toneClass = state.tone === "ok" ? "text-emerald-700" : state.tone === "note" ? "text-amber-800" : "text-red-700";
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="tabular-nums text-slate-600">
        {t("bts.sections.questions", { count: saved.questions_count || 0 })} · {t("bts.sections.points", { count: saved.points || 0 })}
      </span>
      <span className={`inline-flex items-center gap-1 ${toneClass}`} title={state.title || undefined}>
        <Icon className="h-4 w-4" aria-hidden="true" />
        {state.text}
      </span>
    </span>
  );
}

/**
 * The booklet's subjects: in a draft, a confirmed variant per subject (or none) in the booklet's order, saved
 * together (PUT sections). The subjects and their variants come from GET sources/ (the folder's subjects of
 * the booklet's stream and of both); `sourcesKey` changing reads them again. Otherwise the sections, read-only.
 * `onDirtyChange("sections", unsaved)` tells the page whether the list has unsaved changes.
 */
export default function BookletSections({ booklet, onSaved, onStale, sourcesKey, onDirtyChange }) {
  const [t] = useTranslation();
  // From md up a row shows its arrows beside the variant; phones move a subject from the row's "⋯" (OverflowMenu's
  // own breakpoint), next to a full-width variant.
  const wide = useMediaQuery("(min-width: 768px)");
  const draft = booklet.status === "draft";
  const active = booklet.status === "active";
  const savedSections = useMemo(() => booklet.check?.sections || [], [booklet.check]);
  const issues = booklet.check?.issues;
  const saved = useMemo(() => savedSections.map((s) => s.variant?.uuid).filter(Boolean), [savedSections]);
  const savedKey = saved.join(",");

  const [sources, setSources] = useState({ key: "", data: null, error: false });
  const [retry, setRetry] = useState(0);
  const [chosen, setChosen] = useState(saved);
  const baseRef = useRef(saved);
  // The list someone else saved while the user changes theirs (it stays theirs until they save or take it).
  const [conflict, setConflict] = useState(null);
  const [picks, setPicks] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const focusRef = useRef(null);
  const focusAfterRender = useFocusAfterRender();

  const folder = booklet.folder?.uuid;
  const { grade, language } = booklet;
  const key = draft && folder && grade ? `${folder}:${grade}:${language}:${sourcesKey}:${retry}` : "";

  useEffect(() => {
    if (!key) return undefined;
    const controller = new AbortController();
    let active = true;
    getBookletSources({ folder, grade, language }, { signal: controller.signal })
      .then((data) => active && setSources({ key, data, error: false }))
      .catch((err) => {
        if (active && err?.name !== "CanceledError") setSources((prev) => ({ key, data: prev.data, error: true }));
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, folder, grade, language]);

  // The saved sections changed (saved here, or read again): unsaved choices stay, else the saved ones show.
  useEffect(() => {
    const before = baseRef.current.join(",");
    baseRef.current = saved;
    if (chosen.join(",") === before) {
      setChosen(saved);
      setConflict(null);
    } else if (savedKey !== before) {
      setConflict(saved);
    }
  }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a move, the moved row's button keeps the focus (or its other arrow, at an end of the list).
  useLayoutEffect(() => {
    const target = focusRef.current;
    if (!target) return;
    focusRef.current = null;
    const element = document.getElementById(target.id);
    if (element && !element.disabled) element.focus();
    else if (target.fallback) document.getElementById(target.fallback)?.focus();
  }, [chosen]);

  const subjects = useMemo(() => sources.data?.subjects || [], [sources.data]);
  // Every variant the page knows: the sources', then the saved sections' (a variant gone from the sources).
  const variants = useMemo(() => {
    const map = new Map();
    savedSections.forEach((s) => s.variant && map.set(s.variant.uuid, { ...s.variant, questions_count: s.questions_count }));
    subjects.forEach((subject) => subject.variants?.forEach((v) => map.set(v.uuid, { ...v, folder: subject.folder })));
    return map;
  }, [savedSections, subjects]);
  const subjectOf = (uuid) => variants.get(uuid)?.folder || null;
  const savedOf = (uuid) => savedSections.find((s) => s.variant?.uuid === uuid) || null;

  const dirty = chosen.join(",") !== savedKey;
  const unsaved = draft && dirty;
  useEffect(() => {
    onDirtyChange?.("sections", unsaved);
  }, [unsaved, onDirtyChange]);

  const chosenFolders = new Set(chosen.map((uuid) => subjectOf(uuid)?.uuid).filter(Boolean));
  const others = subjects.filter((subject) => !chosenFolders.has(subject.folder.uuid));
  const confirmedOf = (subject) => (subject?.variants || []).filter((v) => v.status === "confirmed");
  // The variant a subject of the add list offers: the one picked, while it is still confirmed, else the first.
  const pickOf = (subject) => {
    const confirmed = confirmedOf(subject);
    const pick = picks[subject.folder.uuid];
    return confirmed.some((v) => v.uuid === pick) ? pick : confirmed[0]?.uuid || "";
  };
  // A row is its subject's (another variant of it keeps the row, its select and the focus), else its variant's.
  const rowKeys = [];
  chosen.forEach((uuid) => {
    const subject = subjectOf(uuid)?.uuid;
    rowKeys.push(subject && !rowKeys.includes(subject) ? subject : uuid);
  });

  const clearErrors = (uuid) => {
    if (error) setError("");
    if (uuid && rowErrors[uuid]) setRowErrors((prev) => ({ ...prev, [uuid]: "" }));
  };
  const move = (index, delta) => {
    const uuid = chosen[index];
    const next = [...chosen];
    next.splice(index, 1);
    next.splice(index + delta, 0, uuid);
    const action = delta < 0 ? "up" : "down";
    focusRef.current = { id: `bts-sec-${uuid}-${action}`, fallback: `bts-sec-${uuid}-${delta < 0 ? "down" : "up"}` };
    setChosen(next);
    clearErrors();
  };
  const remove = (index) => {
    const uuid = chosen[index];
    const subject = subjectOf(uuid);
    focusRef.current = { id: subject ? `bts-add-${subject.uuid}` : TITLE_ID, fallback: TITLE_ID };
    setChosen(chosen.filter((_, i) => i !== index));
    clearErrors(uuid);
  };
  const replace = (index, uuid) => {
    clearErrors(chosen[index]);
    setChosen(chosen.map((value, i) => (i === index ? uuid : value)));
  };
  const add = (subject) => {
    const uuid = pickOf(subject);
    if (!uuid) return;
    focusRef.current = { id: `bts-sec-${uuid}-select`, fallback: TITLE_ID };
    setChosen([...chosen, uuid]);
    clearErrors();
  };
  // Back to the saved list; the bar goes, the focus goes to the card's title.
  const reset = () => {
    setChosen(saved);
    setRowErrors({});
    setError("");
    setConflict(null);
    document.getElementById(TITLE_ID)?.focus();
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setRowErrors({});
    try {
      const data = await setBookletSections(booklet.uuid, chosen, booklet.revision);
      baseRef.current = chosen;
      setConflict(null);
      onSaved(data);
      toast.success(t("bts.sections.saved"));
      // The bar with the button goes away: the focus goes to the card's title.
      focusAfterRender(TITLE_ID);
    } catch (err) {
      const code = getApiErrorCode(err);
      if (STALE.includes(code)) {
        // Read again: the list (kept, or read-only now) is to be looked at before saving again.
        onStale(err);
        focusAfterRender(TITLE_ID);
      } else {
        const body = err?.response?.data || {};
        const message =
          code === "not_found" && body.variant ? t("bts.sections.variant_gone") : bookletErrorMessage(t, err, "bts.sections.save_failed");
        // A row of the list shows its own error; the bar under the list points at it.
        const onRow = body.variant && chosen.includes(body.variant);
        if (onRow) setRowErrors({ [body.variant]: message });
        setError(onRow ? t("bts.sections.row_failed") : message);
        focusAfterRender(onRow ? `bts-sec-${body.variant}-select` : SAVE_ID, TITLE_ID);
      }
    } finally {
      setSaving(false);
    }
  };

  const totals = savedSections.reduce(
    (sum, s) => ({ questions: sum.questions + (s.questions_count || 0), points: sum.points + (s.points || 0) }),
    { questions: 0, points: 0 }
  );

  // In a new tab, as its icon says: what is typed here stays.
  const variantLink = (subjectFolder, uuid, name) =>
    subjectFolder?.uuid ? (
      <Link
        to={variantHref(subjectFolder.uuid, uuid)}
        target="_blank"
        rel="noopener noreferrer"
        className={`${iconButtonClass} max-sm:h-11 max-sm:w-11`}
        aria-label={t("bts.sections.open_variant", { name })}
        title={t("bts.sections.open_variant", { name })}
      >
        <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
      </Link>
    ) : null;

  // The read-only list (an active or archived booklet).
  if (!draft) {
    return (
      <section aria-labelledby={TITLE_ID} className={`${cardClass} p-4 sm:p-5`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 id={TITLE_ID} tabIndex={-1} className={`${sectionTitleClass} outline-none`}>
            {t("bts.sections.title")}
          </h3>
          {savedSections.length > 0 && (
            <p className="text-sm tabular-nums text-slate-500">
              {t("bts.sections.totals", { count: savedSections.length, questions: totals.questions, points: totals.points })}
            </p>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-500">{t("bts.sections.read_only")}</p>
        {savedSections.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">{t("bts.sections.none")}</p>
        ) : (
          <ol className="mt-3 space-y-2">
            {savedSections.map((s, index) => (
              <li key={s.exam_test || s.variant.uuid} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-600">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="break-words font-medium text-slate-900">
                    {s.variant.folder?.name}
                    <span className="font-normal text-slate-500"> · {s.variant.name}</span>
                  </p>
                  <p className="mt-0.5 text-xs">
                    <SectionStats saved={s} issues={issues} sections={savedSections} active={active} />
                  </p>
                </div>
                {variantLink(s.variant.folder, s.variant.uuid, s.variant.name)}
              </li>
            ))}
          </ol>
        )}
      </section>
    );
  }

  const loadingSources = key && sources.key !== key && !sources.data;
  const conflictList = (conflict || [])
    .map((uuid) => {
      const section = savedOf(uuid);
      return section?.variant?.folder?.name || section?.variant?.name || variants.get(uuid)?.name || "";
    })
    .filter(Boolean)
    .join(", ");

  return (
    <section aria-labelledby={TITLE_ID} className={`${cardClass} p-4 sm:p-5`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={TITLE_ID} tabIndex={-1} className={`${sectionTitleClass} outline-none`}>
          {t("bts.sections.title")}
        </h3>
        {savedSections.length > 0 && (
          <p className="text-sm tabular-nums text-slate-500">
            {t("bts.sections.totals", { count: savedSections.length, questions: totals.questions, points: totals.points })}
          </p>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500">{t("bts.sections.intro")}</p>

      {sources.error && (
        <p role="alert" className="mt-3 flex flex-wrap items-center gap-2 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("bts.sections.sources_error")}
        </p>
      )}

      {conflict && dirty && (
        <div role="status" className="mt-3 flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5 text-sm text-amber-900 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <p className="flex min-w-0 items-start gap-2 break-words">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <span>{t("bts.conflict.sections", { list: conflictList || t("bts.conflict.no_sections") })}</span>
          </p>
          <button type="button" onClick={reset} disabled={saving} className={`${linkButtonClass} ${linkTapClass} -my-1 shrink-0 self-start`}>
            {t("bts.conflict.take_sections")}
          </button>
        </div>
      )}

      {chosen.length === 0 ? (
        <p className="mt-3 rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
          {t("bts.sections.empty")}
        </p>
      ) : (
        <ol className="mt-3 space-y-2" aria-label={t("bts.sections.order_label")}>
          {chosen.map((uuid, index) => {
            const info = variants.get(uuid);
            const subjectFolder = info?.folder || null;
            const subject = subjects.find((s) => s.folder.uuid === subjectFolder?.uuid);
            const options = subject?.variants || [];
            const name = info?.name || uuid;
            const savedRow = savedOf(uuid);
            const rowError = rowErrors[uuid];
            const subjectName = subjectFolder?.name || t("bts.sections.unknown_subject");
            // The variant in the bank opens in a new tab, as its link did: what is typed here stays.
            const href = subjectFolder?.uuid ? variantHref(subjectFolder.uuid, uuid) : null;
            const menuId = `bts-sec-${uuid}-actions`;
            const fromMenu = (action) => () => {
              action();
              focusAfterMenu(menuId);
            };
            const rowActions = [
              !wide && { key: "up", label: t("bts.sections.up_short"), Icon: ArrowUpIcon, onClick: fromMenu(() => move(index, -1)), disabled: index === 0 || saving },
              !wide && {
                key: "down",
                label: t("bts.sections.down_short"),
                Icon: ArrowDownIcon,
                onClick: fromMenu(() => move(index, 1)),
                disabled: index === chosen.length - 1 || saving,
              },
              href && {
                key: "open",
                label: t("bts.sections.open_short"),
                Icon: ArrowTopRightOnSquareIcon,
                onClick: fromMenu(() => window.open(href, "_blank", "noopener,noreferrer")),
              },
              // Its own focus: the subject's "+ Add" below (remove).
              { key: "remove", label: t("bts.sections.remove_short"), Icon: XMarkIcon, danger: true, onClick: () => remove(index), disabled: saving },
            ];
            return (
              <li
                key={rowKeys[index]}
                className={`${subjectRowClass} rounded-xl border px-3 py-2.5 ${rowError ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}
              >
                <div className={`${subjectClass} flex items-start gap-3`}>
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold tabular-nums text-blue-700">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="break-words font-medium text-slate-900">{subjectName}</p>
                    <p className="mt-0.5 text-xs">
                      <SectionStats saved={savedRow} info={info} issues={issues} sections={savedSections} />
                    </p>
                    {rowError && (
                      <p role="alert" className="mt-1 text-sm text-red-700">
                        {rowError}
                      </p>
                    )}
                  </div>
                </div>
                {/* The variant, the arrows (md up) and "⋯": opening the variant in the bank, taking the subject out
                    (and moving it, on phones). */}
                <div className={`${controlsClass} gap-1.5`}>
                  {options.length > 0 ? (
                    <select
                      id={`bts-sec-${uuid}-select`}
                      value={uuid}
                      onChange={(e) => replace(index, e.target.value)}
                      aria-label={t("bts.sections.variant_of", { subject: subjectName })}
                      className={`${selectClass} min-w-0 flex-1`}
                    >
                      {options.map((v) => (
                        <option key={v.uuid} value={v.uuid} disabled={v.status !== "confirmed" || (v.uuid !== uuid && chosen.includes(v.uuid))}>
                          {v.status === "confirmed"
                            ? t("bts.sections.variant_option", { name: v.name, count: v.questions_count || 0 })
                            : t("bts.sections.variant_option_draft", { name: v.name })}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span id={`bts-sec-${uuid}-select`} tabIndex={-1} className="min-w-0 flex-1 break-words text-sm text-slate-600 outline-none">
                      {name}
                    </span>
                  )}
                  {wide && (
                    <div className="flex items-center">
                      <button
                        id={`bts-sec-${uuid}-up`}
                        type="button"
                        onClick={() => move(index, -1)}
                        disabled={index === 0 || saving}
                        aria-label={t("bts.sections.move_up", { subject: subjectName })}
                        title={t("bts.sections.move_up", { subject: subjectName })}
                        className={rowButtonClass}
                      >
                        <ArrowUpIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        id={`bts-sec-${uuid}-down`}
                        type="button"
                        onClick={() => move(index, 1)}
                        disabled={index === chosen.length - 1 || saving}
                        aria-label={t("bts.sections.move_down", { subject: subjectName })}
                        title={t("bts.sections.move_down", { subject: subjectName })}
                        className={rowButtonClass}
                      >
                        <ArrowDownIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <OverflowMenu items={rowActions} label={t("bts.sections.row_actions", { subject: subjectName })} testId={menuId} className="shrink-0" />
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* The subjects not in the booklet. */}
      <div className="mt-5">
        <h4 className="text-sm font-semibold text-slate-700">{t("bts.sections.add_title")}</h4>
        {loadingSources ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-slate-500" role="status">
            <Spinner className="h-3.5 w-3.5" />
            {t("bts.sections.sources_loading")}
          </p>
        ) : !sources.data ? (
          // Not read (the error above says so): nothing to tell about the folder's subjects yet.
          sources.error && (
            <button type="button" onClick={() => setRetry((n) => n + 1)} className={`${linkButtonClass} ${linkTapClass} mt-1.5`}>
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              {t("bts.retry")}
            </button>
          )
        ) : others.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">{subjects.length ? t("bts.sections.all_added") : t("bts.sections.no_subjects")}</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200">
            {others.map((subject) => {
              const confirmed = confirmedOf(subject);
              const drafts = (subject.variants || []).length - confirmed.length;
              const pick = pickOf(subject);
              return (
                <li key={subject.folder.uuid} className={`${subjectRowClass} px-3 py-2.5`}>
                  <div className={`${subjectClass} text-sm`}>
                    <p className="break-words font-medium text-slate-800">{subject.folder.name}</p>
                    {!confirmed.length && (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {drafts ? t("bts.sections.only_drafts", { count: drafts }) : t("bts.sections.no_variants")}
                      </p>
                    )}
                  </div>
                  {confirmed.length > 0 ? (
                    <div className={`${controlsClass} gap-2`}>
                      {confirmed.length > 1 ? (
                        <select
                          value={pick}
                          onChange={(e) => setPicks((prev) => ({ ...prev, [subject.folder.uuid]: e.target.value }))}
                          aria-label={t("bts.sections.variant_of", { subject: subject.folder.name })}
                          className={`${selectClass} min-w-0 flex-1`}
                        >
                          {confirmed.map((v) => (
                            <option key={v.uuid} value={v.uuid}>
                              {t("bts.sections.variant_option", { name: v.name, count: v.questions_count || 0 })}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="min-w-0 flex-1 break-words text-sm text-slate-600">
                          {t("bts.sections.variant_option", { name: confirmed[0].name, count: confirmed[0].questions_count || 0 })}
                        </span>
                      )}
                      <button
                        id={`bts-add-${subject.folder.uuid}`}
                        type="button"
                        onClick={() => add(subject)}
                        disabled={saving}
                        aria-label={t("bts.sections.add_subject", { subject: subject.folder.name })}
                        className={addLinkClass}
                      >
                        <PlusIcon className="h-4 w-4" aria-hidden="true" />
                        {t("buttons.add")}
                      </button>
                    </div>
                  ) : (
                    <Link
                      id={`bts-add-${subject.folder.uuid}`}
                      to={variantHref(subject.folder.uuid)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${linkButtonClass} ${linkTapClass}`}
                    >
                      {t("bts.sections.build_variant")}
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {sources.data && (
          <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("bts.sections.sources_note")}
          </p>
        )}
      </div>

      {(dirty || error) && (
        <div
          className="sticky bottom-0 -mx-4 -mb-4 mt-5 flex flex-col gap-3 rounded-b-2xl border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:-mb-5 sm:flex-row sm:items-center sm:justify-between sm:px-5"
          aria-live="polite"
        >
          <p className={`text-sm ${error ? "text-red-700" : "text-slate-600"}`} role={error ? "alert" : undefined}>
            {error || t("bts.sections.unsaved")}
          </p>
          {dirty && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={reset} disabled={saving} className={tapClass} />
              <Button id={SAVE_ID} type="button" color="primary" text={t("bts.sections.save")} onClick={save} loading={saving} className={tapClass} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
